import React from 'react';
import { Annotation } from '@/types';

// DB stores integer coords scaled by 10000. Convert to percentages for SVG rendering.
const COORD_SCALE = 10000;

interface AnnotationOverlayProps {
  annotations: Annotation[];
}

const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({ annotations }) => {
  if (!annotations || annotations.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 10000 10000"
      preserveAspectRatio="none"
    >
      {annotations.map((a) => {
        const x = a.x;
        const y = a.y;
        const w = a.width ?? 0;
        const h = a.height ?? 0;

        switch (a.type) {
          case 'arrow': {
            const x2 = x + w;
            const y2 = y + h;
            const angle = Math.atan2(y2 - y, x2 - x);
            const headLen = 200; // in SVG units
            return (
              <g key={a.id}>
                <line
                  x1={x} y1={y} x2={x2} y2={y2}
                  stroke={a.color} strokeWidth={40} strokeLinecap="round"
                />
                <line
                  x1={x2} y1={y2}
                  x2={x2 - headLen * Math.cos(angle - Math.PI / 6)}
                  y2={y2 - headLen * Math.sin(angle - Math.PI / 6)}
                  stroke={a.color} strokeWidth={40} strokeLinecap="round"
                />
                <line
                  x1={x2} y1={y2}
                  x2={x2 - headLen * Math.cos(angle + Math.PI / 6)}
                  y2={y2 - headLen * Math.sin(angle + Math.PI / 6)}
                  stroke={a.color} strokeWidth={40} strokeLinecap="round"
                />
              </g>
            );
          }
          case 'circle':
            return (
              <ellipse
                key={a.id}
                cx={x} cy={y}
                rx={Math.abs(w) || 1} ry={Math.abs(h) || 1}
                stroke={a.color} strokeWidth={40} fill="none"
              />
            );
          case 'rectangle':
            return (
              <rect
                key={a.id}
                x={x} y={y} width={w} height={h}
                stroke={a.color} strokeWidth={40} fill="none"
              />
            );
          case 'text':
            return (
              <g key={a.id}>
                <rect
                  x={x - 20} y={y - 20}
                  width={((a.text?.length ?? 0) * 200) + 40}
                  height={350}
                  fill="rgba(0,0,0,0.5)" rx={20}
                />
                <text
                  x={x} y={y + 250}
                  fill={a.color}
                  fontSize={280}
                  fontWeight="bold"
                  fontFamily="system-ui, sans-serif"
                >
                  {a.text}
                </text>
              </g>
            );
          default:
            return null;
        }
      })}
    </svg>
  );
};

export { COORD_SCALE };
export default AnnotationOverlay;
